import React from 'react';
import { Loader, ExamType, ExamApplicationStatus } from 'components';
import { ApplicationExamDetails, IApplicationExamDetailsProps } from '../../components';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { useLocalErrorHandler } from 'hooks';
import { WriteRecommendations, Rate } from '../../api/ApiClient';

const ApplicationExamDetailsContainer = ({
    examApplicationId,
    onFetchExamApplicationDetails,
    onFetchExamApplications,
    onFetchExamDetails,
    getExamSessionDetails,
    examSessionDetails,
    applicationDetails,
    examDetails,
    writeRecommendations,
    profile,
    setErrorHandler,
    writeEssay,
    onCloseClick,
    getFormatRecommendationsSketch,
}: IProps) => {
    const [processing, setProcessing] = React.useState(false);
    const [recommendationsSketch, setRecommendationsSketch] = React.useState<string | null>(null);

    React.useEffect(() => {
        const fetchDetails = async () => {
            setProcessing(true);
            const examApplicationDetails = await onFetchExamApplicationDetails(examApplicationId);

            if (examApplicationDetails) {
                const { exam } = examApplicationDetails;

                if (examApplicationDetails.status === ExamApplicationStatus.NotPassed) {
                    const recommendations = await getFormatRecommendationsSketch(examApplicationId);
                    setRecommendationsSketch(recommendations);
                }

                if (exam.type !== ExamType.Recertification) {
                    await getExamSessionDetails(examApplicationId);
                }

                await onFetchExamDetails(exam.id);
            }
            setProcessing(false);
        };

        fetchDetails();
    }, []);

    useLocalErrorHandler(setErrorHandler);

    const createExamDetailsProps = () => {
        if (!examDetails || !applicationDetails || !examSessionDetails) return null;

        const examId = applicationDetails.exam.id;

        const saveRecommendation = async (recommendations: string, passed?: boolean) => {
            if (passed !== undefined) {
                const body = new Rate({ examApplicationId, recommendations, passed });
                await writeEssay({ examId, body });
            } else {
                const body = new WriteRecommendations({ examApplicationId, recommendations });
                await writeRecommendations({ examId, body });
            }
            await onFetchExamApplicationDetails(examApplicationId);
            await onFetchExamApplications({ examId, pageSize: 500 });

            if (onCloseClick) {
                onCloseClick();
            }
        };

        const onClose = () => {
            if (onCloseClick) {
                onCloseClick();
            }
        };

        const chairman = !!(profile && examDetails.experts.find((expert: IExamExperts) => expert.expertId === profile.id && expert.chairman));

        const examDetailsProps: IApplicationExamDetailsProps = {
            examDetails,
            saveRecommendation,
            recommendations: applicationDetails.recommendations || recommendationsSketch || '',
            chairman,
            examSessionDetails,
            applicantStatus: applicationDetails.status,
            finalized: applicationDetails.finalized,
            onClose,
        };

        return examDetailsProps;
    };

    const props = createExamDetailsProps();

    return !processing && examDetails && examSessionDetails && applicationDetails ? (
        <>
            {props ? <ApplicationExamDetails {...props} /> : null}
        </>
    ) : <Loader fullHeight />;
};

const mapState = (state: IRootState) => ({
    applicationDetails: state.examApplications.examApplicationDetails,
    examSessionDetails: state.examApplications.examSessionDetails,
    examDetails: state.exams.examDetails,
    profile: state.auth && state.auth.auth ? state.auth.auth.profile : null,
});

const mapDispatch = (dispatch: Dispatch) => ({
    onPush: (key: string) => dispatch(push(key)),
    onFetchExamApplicationDetails: dispatch.examApplications.fetchExamApplicationDetails,
    getExamSessionDetails: dispatch.examApplications.getExamSessionDetailsAsync,
    onFetchExamDetails: dispatch.exams.fetchExamDetails,
    onFetchExamApplications: dispatch.examApplications.fetchExamApplications,
    writeRecommendations: dispatch.exams.writeRecommendationsAsync,
    getFormatRecommendationsSketch: dispatch.examApplications.formatRecommendationsSketchAsync,
    writeEssay: dispatch.exams.rateAsync,
    setErrorHandler: dispatch.errors.setErrorHandler,
});

export default connect(mapState, mapDispatch)(ApplicationExamDetailsContainer);

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>
type IProps = connectedProps & OwnProps;

interface OwnProps {
    examApplicationId: string;
    onCloseClick?: () => void;
}

interface IExamExperts {
    regNumber: string;
    expertId: string;
    fullName: string;
    email: string;
    region: string;
    active: boolean;
    signed: boolean;
    chairman: boolean;
}
